import { useEffect, useRef } from 'react';

interface PusherConfig {
  key: string;
  cluster: string;
}

export function useOrderRealtime(
  orderIds: number[],
  onStatusChange: (orderId: number, newStatus: string) => void,
  pusherConfig: PusherConfig,
) {
  const echoRef = useRef<any>(null);
  const channelsRef = useRef<Map<number, any>>(new Map());

  useEffect(() => {
    if (orderIds.length === 0 || !pusherConfig.key) return;

    const setupEcho = async () => {
      try {
        const [{ default: Echo }, { default: Pusher }] = await Promise.all([
          import('laravel-echo'),
          import('pusher-js'),
        ]);

        if (!echoRef.current) {
          echoRef.current = new Echo({
            broadcaster: 'pusher',
            Pusher,
            key: pusherConfig.key,
            cluster: pusherConfig.cluster,
            forceTLS: true,
          });
        }

        const echo = echoRef.current;

        orderIds.forEach(id => {
          if (channelsRef.current.has(id)) return;

          const channel = echo.private(`orders.${id}`)
            .listen('OrderCancelled', () => {
              onStatusChange(id, 'Cancelado');
            });

          channelsRef.current.set(id, channel);
        });

        channelsRef.current.forEach((_, id) => {
          if (!orderIds.includes(id)) {
            echo.leave(`orders.${id}`);
            channelsRef.current.delete(id);
          }
        });
      } catch (error) {
        console.error('Failed to set up real-time updates:', error);
      }
    };

    setupEcho();

    return () => {
      if (echoRef.current) {
        channelsRef.current.forEach((_, id) => {
          echoRef.current.leave(`orders.${id}`);
        });
        channelsRef.current.clear();
      }
    };
  }, [orderIds, pusherConfig, onStatusChange]);
}
